﻿import {debounceAsync, focusNextElement, isNullOrEmpty, Paths, readNestedProperty} from "@2d-front-monorepo/core";
import React, {JSX, useCallback, useEffect, useRef, useState} from "react";
import LabeledControl from "../HtmlControls/LabeledControl";
import ListBox, {ListBoxItemProps} from "../ListBox/ListBox";
import {HtmlRequiredPrefixedControlProps} from "./HtmlControlProps";
import {useLabeledControl} from "./LabeledControlData";

export interface AutocompleteProps<TValue> extends Omit<HtmlRequiredPrefixedControlProps<HTMLInputElement>, "value"> {
	itemLabel: Paths<TValue>;
	value?: TValue | null | undefined;
	fetch: (query: string) => Promise<TValue[]>;
	onSelect: (value: TValue) => void;
	placeholder?: string;
	autoFocus?: boolean;
	noResultMessage?: string;
	onBlurSelectSingle?: boolean;
}

export default function Autocomplete<TValue = never>(props: AutocompleteProps<TValue>): JSX.Element {
	const {
		label,
		name,
		value,
		id,
		column,
		disabled,
		hidden,
		onChange,
		onBlur,
		onClick,
		ref,

		required,
		hint,
		error,
		inlineError,

		controlPrefix,
		controlSuffix,

		itemLabel,
		fetch,
		onSelect,
		placeholder,
		autoFocus,
		onBlurSelectSingle,
		noResultMessage
	} = props;

	const {componentId, ariaRef} = useLabeledControl(id, hint, error);

	const internalRef = useRef<HTMLInputElement>(null);
	const inputRef = ref ?? internalRef;
	const [loading, setLoading] = useState<boolean>(false);
	const [listBoxItems, setListBoxItems] = useState<ListBoxItemProps<TValue>[]>([]);
	const [listBoxVisible, setListBoxVisible] = useState<boolean>(false);
	const inputText = readNestedProperty(value, itemLabel);

	const updateInputValue = useCallback(function (newValue: TValue) {
		if (inputRef.current) {
			inputRef.current.value = readNestedProperty(newValue, itemLabel);
		}

		onSelect(newValue);

	}, [itemLabel, inputRef, onSelect]);

	const onBlurInternal = useCallback(function (e: React.FocusEvent<HTMLInputElement>) {
		if (onBlurSelectSingle && listBoxItems.length === 1) {
			// Check if focus is moving to the listBox (Tab navigation)
			const focusMovingToListBox = e.relatedTarget && e.relatedTarget instanceof HTMLElement && e.relatedTarget.closest("menu.list-box");

			setListBoxVisible(false);
			updateInputValue(listBoxItems[0].value);

			if (focusMovingToListBox) {
				// If focus was moving to listBox via Tab, move it to the next focusable element
				focusNextElement(e.currentTarget);
			}
		}

		onBlur?.(e);
	}, [onBlurSelectSingle, onBlur, updateInputValue, listBoxItems]);

	const selectedCallback = useCallback(function (value: TValue): void {
		setLoading(false);
		setListBoxVisible(false);

		if (value == null) {
			return;
		}

		updateInputValue(value);

	}, [updateInputValue, setLoading, setListBoxVisible]);

	// Bumped on every input: a response whose generation no longer matches was superseded
	// (typed more, or cleared the field) and must not reopen the list with stale results.
	const queryGenerationRef = useRef(0);

	const dataQuery = useCallback(debounceAsync(async function (generation: number, query: string) {
			let result: TValue[];
			try {
				result = await fetch(query);
			} catch {
				if (generation === queryGenerationRef.current) {
					setLoading(false);
				}
				return;
			}
			if (generation !== queryGenerationRef.current) {
				return;
			}
			setLoading(false);
			if (result.length === 0) {
				setListBoxItems([{label: noResultMessage ?? "No results found", value: null!}]);
			} else {
				setListBoxItems(result.map(x => ({label: readNestedProperty(x, itemLabel), value: x})));
			}
			setListBoxVisible(true);
		}, 350), [fetch, noResultMessage, itemLabel]
	);

	const onInputChange = useCallback(function (e: React.InputEvent<HTMLInputElement>) {
		const value = e.currentTarget.value;
		setListBoxVisible(false);
		const generation = ++queryGenerationRef.current;

		if (isNullOrEmpty(value)) {
			setLoading(false);

		} else {
			void dataQuery(generation, value);
			setLoading(true);
		}

		onChange?.(e);

	}, [setListBoxVisible, setLoading, dataQuery, onChange]);

	useEffect(function () {
		if (!inputRef.current) {
			return
		}
		const newLabel = readNestedProperty(value, itemLabel);
		inputRef.current.value = newLabel == null ? "" : newLabel;
	}, [value, inputText, itemLabel]);

	return <LabeledControl label={label}
	                       type="autocomplete-input"
	                       id={componentId}
	                       required={required}
	                       disabled={disabled}
	                       clickable={disabled && onClick != null}
	                       column={column}
	                       error={error}
	                       inlineError={inlineError}
	                       hint={hint}
	                       hidden={hidden}
	                       ariaRef={ariaRef}
	                       controlPrefix={controlPrefix}
	                       controlSuffix={controlSuffix}
	                       footer={listBoxVisible && <ListBox items={listBoxItems} onSelect={selectedCallback}/>}>

		<input type="search"
		       id={componentId}
		       ref={inputRef}
		       name={name}
		       readOnly={disabled}
		       onClick={onClick}
		       onInput={onInputChange}
		       placeholder={placeholder}
		       autoFocus={autoFocus}
		       onBlur={onBlurInternal}
		       defaultValue={inputText}
		       aria-describedby={ariaRef.current?.describedBy}
		       aria-invalid={ariaRef.current?.invalid}
		       aria-required={required}
		/>
		{loading && <span className="field__input__search--loading"></span>}
	</LabeledControl>;
};
